import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const ParentTasks = () => {
  const routes = all_routes;

  // Mock data for children
  const [children] = useState([
    { id: "1", name: "Emma Johnson", grade: "10th", class: "10-A" },
    { id: "2", name: "James Johnson", grade: "8th", class: "8-B" },
  ]);

  const [selectedChildId, setSelectedChildId] = useState("1");
  const [activeTab, setActiveTab] = useState("pending");

  const selectedChild = children.find((child) => child.id === selectedChildId);

  // Mock tasks data
  const tasksData = {
    pending: [
      {
        id: "1",
        title: "Permission Slip - Field Trip",
        description: "Science Museum visit next Friday",
        type: "permission",
        dueDate: "2024-08-08",
        priority: "high",
        action: "Sign & Submit",
      },
      {
        id: "2",
        title: "Annual Health Form",
        description: "Complete medical information for school records",
        type: "form",
        dueDate: "2024-08-15",
        priority: "medium",
        action: "Fill Form",
      },
      {
        id: "3",
        title: "Parent-Teacher Conference",
        description: "Schedule meeting with Math teacher",
        type: "meeting",
        dueDate: "2024-08-10",
        priority: "high",
        action: "Schedule",
      },
      {
        id: "4",
        title: "Lunch Menu Selection",
        description: "Choose meals for next month",
        type: "form",
        dueDate: "2024-08-12",
        priority: "low",
        action: "Select",
      },
    ],
    overdue: [
      {
        id: "5",
        title: "Emergency Contact Update",
        description: "Update emergency contact information",
        type: "form",
        dueDate: "2024-08-01",
        priority: "high",
        action: "Update",
      },
      {
        id: "6",
        title: "Photo Permission",
        description: "Consent for school photography",
        type: "permission",
        dueDate: "2024-07-30",
        priority: "medium",
        action: "Approve",
      },
    ],
    completed: [
      {
        id: "7",
        title: "Transportation Form",
        description: "Bus route confirmation submitted",
        type: "form",
        completedDate: "2024-08-02",
        status: "approved",
      },
      {
        id: "8",
        title: "Activity Consent",
        description: "Sports participation approved",
        type: "permission",
        completedDate: "2024-07-28",
        status: "approved",
      },
    ],
  };

  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case "permission":
        return "ti-shield-check";
      case "form":
        return "ti-file-text";
      case "meeting":
        return "ti-calendar";
      case "payment":
        return "ti-credit-card";
      default:
        return "ti-clipboard-list";
    }
  };

  const getTaskTypeColor = (type: string) => {
    switch (type) {
      case "permission":
        return "success";
      case "form":
        return "primary";
      case "meeting":
        return "warning";
      case "payment":
        return "info";
      default:
        return "secondary";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "danger";
      case "medium":
        return "warning";
      case "low":
        return "info";
      default:
        return "secondary";
    }
  };

  const renderTaskCard = (task: any) => (
    <div key={task.id} className="card mb-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <div
              className={`avatar avatar-sm bg-${getTaskTypeColor(
                task.type
              )}-transparent me-3`}
            >
              <i
                className={`ti ${getTaskTypeIcon(
                  task.type
                )} text-${getTaskTypeColor(task.type)}`}
              ></i>
            </div>
            <div>
              <h6 className="mb-1">{task.title}</h6>
              <p className="text-muted mb-0">
                <span
                  className={`badge bg-${getTaskTypeColor(
                    task.type
                  )}-transparent me-2`}
                >
                  {task.type}
                </span>
                {selectedChild?.name}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {task.priority && (
              <span className={`badge bg-${getPriorityColor(task.priority)}`}>
                {task.priority}
              </span>
            )}
            {task.status && (
              <span className="badge bg-success">{task.status}</span>
            )}
          </div>
        </div>

        <p className="text-muted mb-3">{task.description}</p>

        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <i className="ti ti-calendar me-2 text-muted"></i>
            <span
              className={`${
                task.dueDate && new Date(task.dueDate) < new Date()
                  ? "text-danger"
                  : "text-muted"
              }`}
            >
              {task.completedDate
                ? `Completed: ${task.completedDate}`
                : `Due: ${task.dueDate}`}
            </span>
          </div>
          {task.action && (
            <button className={`btn btn-${getTaskTypeColor(task.type)} btn-sm`}>
              {task.action}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header with Child Selector */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Tasks & To-Do</h3>
              <p className="text-muted mb-0">
                Manage permissions, forms, and deadlines
              </p>
            </div>
            <div className="col-auto">
              {children.length > 1 && (
                <div className="dropdown">
                  <button
                    className="btn btn-outline-primary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-user me-2"></i>
                    {selectedChild?.name}
                  </button>
                  <ul className="dropdown-menu">
                    {children.map((child) => (
                      <li key={child.id}>
                        <button
                          className={`dropdown-item ${
                            selectedChildId === child.id ? "active" : ""
                          }`}
                          onClick={() => setSelectedChildId(child.id)}
                        >
                          {child.name} - {child.grade} {child.class}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-4 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-warning mb-2">
                    <i className="ti ti-clock text-white fs-2"></i>
                  </div>
                  <h4 className="text-warning">{tasksData.pending.length}</h4>
                  <p className="mb-0">Pending Tasks</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6">
              <div className="card bg-danger-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-danger mb-2">
                    <i className="ti ti-alert-triangle text-white fs-2"></i>
                  </div>
                  <h4 className="text-danger">{tasksData.overdue.length}</h4>
                  <p className="mb-0">Overdue</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-success mb-2">
                    <i className="ti ti-check text-white fs-2"></i>
                  </div>
                  <h4 className="text-success">{tasksData.completed.length}</h4>
                  <p className="mb-0">Completed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Quick Actions</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3 col-sm-6 mb-2">
                      <button className="btn btn-success w-100">
                        <i className="ti ti-shield-check me-2"></i>
                        New Permission
                      </button>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-2">
                      <button className="btn btn-primary w-100">
                        <i className="ti ti-file-text me-2"></i>
                        Fill Form
                      </button>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-2">
                      <button className="btn btn-warning w-100">
                        <i className="ti ti-calendar me-2"></i>
                        Schedule Meeting
                      </button>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-2">
                      <button className="btn btn-info w-100">
                        <i className="ti ti-file-download me-2"></i>
                        Download Forms
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks Tabs */}
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs" role="tablist">
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "pending" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("pending")}
                  >
                    Pending ({tasksData.pending.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "overdue" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("overdue")}
                  >
                    Overdue ({tasksData.overdue.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "completed" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("completed")}
                  >
                    Completed ({tasksData.completed.length})
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
              {/* Pending Tasks */}
              {activeTab === "pending" && (
                <div>
                  {tasksData.pending.length > 0 ? (
                    tasksData.pending.map((task) => renderTaskCard(task))
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar avatar-lg bg-success-transparent mb-3">
                        <i className="ti ti-check fs-2 text-success"></i>
                      </div>
                      <h5>No pending tasks</h5>
                      <p className="text-muted">All tasks are up to date!</p>
                    </div>
                  )}
                </div>
              )}

              {/* Overdue Tasks */}
              {activeTab === "overdue" && (
                <div>
                  {tasksData.overdue.length > 0 ? (
                    tasksData.overdue.map((task) => renderTaskCard(task))
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar avatar-lg bg-success-transparent mb-3">
                        <i className="ti ti-check fs-2 text-success"></i>
                      </div>
                      <h5>No overdue tasks</h5>
                      <p className="text-muted">Great job staying on track!</p>
                    </div>
                  )}
                </div>
              )}

              {/* Completed Tasks */}
              {activeTab === "completed" && (
                <div>
                  {tasksData.completed.length > 0 ? (
                    tasksData.completed.map((task) => renderTaskCard(task))
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar avatar-lg bg-info-transparent mb-3">
                        <i className="ti ti-clipboard-list fs-2 text-info"></i>
                      </div>
                      <h5>No completed tasks</h5>
                      <p className="text-muted">
                        Completed tasks will appear here.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParentTasks;
